// packages/widget/src/components/steps/ticket-total-label.ts
//
// UAT round 93 X1 — the words beside the ticket total for a TIERED basket.
//
// A #652 basket is priced by LINES: the server sends `lines[]` and no single
// `unitPriceCents`, so the old "R x pp × N guests" label rendered "R 0,00 pp".
// This builds the label from the server's own lines instead — counts and tier
// names only. It formats no money and multiplies nothing: the total next to it
// is the server's `totalCents`, and that stays the only number that matters.

/** The parts of a server quote line this label reads. */
export interface TicketTotalLine {
  tierId: string;
  tierLabel?: string;
  qty: number;
  kidsQty: number;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * "2 × VIP + 1 × General" (+ " + 1 kid"), from the server's quote lines.
 *
 * A line's `qty` is its SEATS, children included; a child pays one flat rate
 * whatever tier they sit on, so the honest breakdown is adults per tier plus
 * the children once. When any tier cannot be named, it says "N tickets".
 * Returns null when there are no lines (an untiered quote — the caller keeps
 * its "R x pp × N guests" label).
 *
 * `fallbackLabel` resolves a tier the server line did not name (the service
 * step has the event's tier config; the occurrence picker does not).
 */
export function tieredTicketTotalLabel(
  lines: readonly TicketTotalLine[] | null | undefined,
  fallbackLabel?: (tierId: string) => string | undefined,
): string | null {
  if (!lines || lines.length === 0) return null;

  const seats = lines.reduce((n, l) => n + Math.max(0, l.qty || 0), 0);
  const kids = lines.reduce((n, l) => n + Math.max(0, l.kidsQty || 0), 0);

  const parts: string[] = [];
  for (const l of lines) {
    const adults = Math.max(0, (l.qty || 0) - (l.kidsQty || 0));
    if (adults === 0) continue;
    const name = (l.tierLabel ?? '').trim() || (fallbackLabel?.(l.tierId) ?? '').trim();
    if (!name) return plural(seats, 'ticket', 'tickets');
    parts.push(`${adults} × ${name}`);
  }
  if (kids > 0) parts.push(plural(kids, 'kid', 'kids'));
  return parts.length > 0 ? parts.join(' + ') : plural(seats, 'ticket', 'tickets');
}
